import React from "react";
import { Link } from "react-router-dom";

import { formatPercent } from "utils/format";
import { useRoutes } from "utils/routes";
import PanelSection from "components/PanelSection";

import { ServerQueryOverview_getQueryOverviewStatsByDatabase as SummaryType } from "../types/ServerQueryOverview";
import { makeFilter } from "utils/filter";
import Grid, { MsCell, NumberCell } from "components/Grid";
import { useQueryStringSearch } from "components/QueryStringSearch";

type RowDataType = {
  databaseId: string | null | undefined;
  databaseName: string | null | undefined;
} & SummaryType;

type Props = {
  data: SummaryType[];
};

const ServerQueryOverviewTable: React.FunctionComponent<Props> = ({ data }) => {
  const [searchTerm, SearchInput] = useQueryStringSearch("q");

  const tableData = data.map((inputData: SummaryType): RowDataType => {
    return {
      ...inputData,
      databaseId: (inputData.database && inputData.database.id) || null,
      databaseName: (inputData.database && inputData.database.datname) || null,
    };
  });

  const gridData = tableData.filter(makeFilter(searchTerm, "databaseName"));

  return (
    <>
      <PanelSection className="flex justify-between">
        <strong>Select a database to show detailed query statistics:</strong>
        <SearchInput />
      </PanelSection>
      <Grid
        striped
        defaultSortBy="queriesPerSecond"
        data={gridData}
        columns={[
          {
            field: "databaseName",
            header: "Database",
            width: "minmax(30%,1fr)",
            renderer: DatabaseNameCell,
          },
          {
            field: "avgTime",
            header: "Avg Time (ms)",
            width: "minmax(12%,120px)",
            style: "number",
            defaultSortOrder: "DESC",
            renderer: MsCell,
          },
          {
            field: "queriesPerSecond",
            header: "Queries / Second",
            width: "minmax(12%,120px)",
            style: "number",
            defaultSortOrder: "DESC",
            nullValue: "n/a",
            renderer: NumberCell,
          },
          {
            field: "pctOfTotalIo",
            header: "% of All I/O",
            width: "minmax(12%,120px)",
            style: "number",
            defaultSortOrder: "DESC",
            nullValue: "n/a",
            renderer: function PctOfTotalIoCell({ fieldData }) {
              return formatPercent(fieldData / 100.0);
            },
          },
          {
            field: "pctOfTotal",
            header: "% of All Runtime",
            width: "minmax(12%,120px)",
            style: "number",
            defaultSortOrder: "DESC",
            renderer: function PctOfTotalCell({ fieldData }) {
              return formatPercent(fieldData / 100.0);
            },
          },
        ]}
      />
    </>
  );
};

const DatabaseNameCell: React.FunctionComponent<{ rowData: RowDataType }> = ({
  rowData,
}) => {
  const { databaseQueries } = useRoutes();
  return (
    <span>
      {rowData.database && !rowData.database.hidden && (
        <Link to={databaseQueries(rowData.database.id)}>
          {rowData.database.datname}
        </Link>
      )}
      {rowData.database && rowData.database.hidden && rowData.database.datname}
    </span>
  );
};

export default ServerQueryOverviewTable;
